import type {
  SpreadsheetChartArtifactType,
  SpreadsheetSparklineType,
} from '../protocol/spreadsheetEngineProtocol'
import { EXCEL_RANGE_CHART_TYPE_LABELS } from './derivedArtifactPreview'
import type { SpreadsheetCommandId } from './commandRegistry'

export type FormattingVisualCommandState = {
  enabled: boolean
  disabledReason: string
}

export type ConditionalFormattingMenuDescriptor = {
  id: 'new-rule' | 'clear-selection'
  label: string
  disabled?: boolean
}

export type SparklineCommandDescriptor = {
  id: Extract<SpreadsheetCommandId,
    | 'insert-sparkline-line'
    | 'insert-sparkline-column'
    | 'insert-sparkline-win-loss'
  >
  sparklineType: SpreadsheetSparklineType
}

export type DerivedArtifactCommandDescriptor = {
  id: Extract<SpreadsheetCommandId, 'insert-chart' | 'insert-pivot'>
  artifactKind: 'chart' | 'pivot'
}

export type ExcelRangeChartMenuDescriptor = {
  id: `insert-chart-${SpreadsheetChartArtifactType}`
  label: string
  title: string
  chartType: SpreadsheetChartArtifactType
  disabled?: boolean
}

export const SPARKLINE_COMMANDS: SparklineCommandDescriptor[] = [
  { id: 'insert-sparkline-line', sparklineType: 'line' },
  { id: 'insert-sparkline-column', sparklineType: 'column' },
  { id: 'insert-sparkline-win-loss', sparklineType: 'winLoss' },
]

export const DERIVED_ARTIFACT_COMMANDS: DerivedArtifactCommandDescriptor[] = [
  { id: 'insert-chart', artifactKind: 'chart' },
  { id: 'insert-pivot', artifactKind: 'pivot' },
]

export const EXCEL_RANGE_CHART_MENU_TYPES: SpreadsheetChartArtifactType[] = [
  'column',
  'bar',
  'line',
  'area',
  'pie',
  'scatter',
]

export function sparklineTypeForCommand(commandId: SpreadsheetCommandId): SpreadsheetSparklineType | null {
  return SPARKLINE_COMMANDS.find((command) => command.id === commandId)?.sparklineType ?? null
}

export function derivedArtifactKindForCommand(commandId: SpreadsheetCommandId): 'chart' | 'pivot' | null {
  return DERIVED_ARTIFACT_COMMANDS.find((command) => command.id === commandId)?.artifactKind ?? null
}

export function buildConditionalFormattingMenuDescriptors(args: {
  canEditSelection?: boolean | null
}): ConditionalFormattingMenuDescriptor[] {
  const canEditSelection = Boolean(args.canEditSelection)
  return [
    {
      id: 'new-rule',
      label: 'New Rule',
    },
    {
      id: 'clear-selection',
      label: 'Clear Rules from Selection',
      disabled: !canEditSelection,
    },
  ]
}

export function buildExcelRangeChartMenuDescriptors(args: {
  canCreateChart?: boolean | null
}): ExcelRangeChartMenuDescriptor[] {
  const disabled = !args.canCreateChart
  return EXCEL_RANGE_CHART_MENU_TYPES.map((chartType) => ({
    id: `insert-chart-${chartType}`,
    label: EXCEL_RANGE_CHART_TYPE_LABELS[chartType],
    title: `Create ${EXCEL_RANGE_CHART_TYPE_LABELS[chartType].toLowerCase()} chart from selection`,
    chartType,
    disabled,
  }))
}

export function buildFormattingVisualCommandPresentation(args: {
  hasSelection: boolean
  hasStreamer: boolean
}): Partial<Record<SpreadsheetCommandId, FormattingVisualCommandState>> {
  const selectionAndStreamer = args.hasSelection && args.hasStreamer
  return {
    'conditional-formatting': {
      enabled: selectionAndStreamer,
      disabledReason: 'Select cells after the spreadsheet engine is ready',
    },
    'insert-chart': {
      enabled: selectionAndStreamer,
      disabledReason: 'Select data cells after the spreadsheet engine is ready',
    },
    'insert-pivot': {
      enabled: selectionAndStreamer,
      disabledReason: 'Select source data cells after the spreadsheet engine is ready',
    },
    'insert-sparkline-line': {
      enabled: selectionAndStreamer,
      disabledReason: 'Select data cells after the spreadsheet engine is ready',
    },
    'insert-sparkline-column': {
      enabled: selectionAndStreamer,
      disabledReason: 'Select data cells after the spreadsheet engine is ready',
    },
    'insert-sparkline-win-loss': {
      enabled: selectionAndStreamer,
      disabledReason: 'Select data cells after the spreadsheet engine is ready',
    },
    'clear-sparklines': {
      enabled: selectionAndStreamer,
      disabledReason: 'Select sparkline cells after the spreadsheet engine is ready',
    },
  }
}
